import { useMemo, useState } from "react";
import type { UiCommandBusV2, UiSnapshotV2 } from "@/ui/runtime/contracts/manager-ui-v2";
import { Badge } from "@/ui/foundation/primitives/badge";
import { Button } from "@/ui/foundation/primitives/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/foundation/primitives/card";
import { DataList } from "@/ui/components/common/data-list";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui/foundation/primitives/table";
import { CirclePoundSterlingIcon } from "lucide-react";
import { ManagerSecondaryPane } from "@/ui/components/manager/manager-layout";
import { clubSections } from "@/ui/components/manager/manager-ui-helpers";

interface UseStaffScreenStateResult {
    selectedClubSection: (typeof clubSections)[number];
    setSelectedClubSection(sectionId: string): void;
}

export function useStaffScreenState(): UseStaffScreenStateResult {
    const [selectedClubSectionId, setSelectedClubSectionId] = useState<string>(clubSections[0].id);

    const selectedClubSection = useMemo(
        () => clubSections.find((item) => item.id === selectedClubSectionId) ?? clubSections[0],
        [selectedClubSectionId]
    );

    return {
        selectedClubSection,
        setSelectedClubSection: setSelectedClubSectionId,
    };
}

interface StaffScreenSecondaryProps {
    state: UseStaffScreenStateResult;
}

export function StaffScreenSecondary({ state }: StaffScreenSecondaryProps) {
    return (
        <ManagerSecondaryPane title="Club">
            <DataList
                items={clubSections}
                getKey={(section) => section.id}
                selectedKey={state.selectedClubSection.id}
                onSelect={(section) => state.setSelectedClubSection(section.id)}
                itemClassName="flex flex-col items-start gap-1"
                renderItem={(section) => (
                    <>
                        <span className="text-sm font-medium">{section.label}</span>
                        <span className="text-xs text-muted-foreground">{section.description}</span>
                    </>
                )}
            />
        </ManagerSecondaryPane>
    );
}

interface StaffScreenMainProps {
    snapshot: UiSnapshotV2;
    commands: UiCommandBusV2;
    state: UseStaffScreenStateResult;
}

export function StaffScreenMain({ snapshot, commands, state }: StaffScreenMainProps) {
    const clubStaff = snapshot.routes.club.staff;
    const weeklyStaffWageThousands = clubStaff.reduce((sum, member) => sum + member.wage, 0);
    const budgetLabel = snapshot.routes.club.financeLabel.replace("Budget: ", "");

    return (
        <div className="grid gap-4">
            <Card>
                <CardHeader>
                    <CardDescription>Club Section</CardDescription>
                    <CardTitle>{state.selectedClubSection.label}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-3 text-sm text-muted-foreground">
                    <p>{state.selectedClubSection.description}</p>
                    <p>{snapshot.routes.club.financeLabel}</p>
                    <div className="grid gap-3 md:grid-cols-3">
                        <Card><CardHeader><CardDescription>Staff</CardDescription><CardTitle>{clubStaff.length}</CardTitle></CardHeader></Card>
                        <Card><CardHeader><CardDescription>Weekly Staff Wages</CardDescription><CardTitle>£{weeklyStaffWageThousands.toLocaleString()}k/w</CardTitle></CardHeader></Card>
                        <Card>
                            <CardHeader>
                                <CardDescription>Budget</CardDescription>
                                <CardTitle className="flex items-center gap-1"><CirclePoundSterlingIcon className="size-4" />{budgetLabel}</CardTitle>
                            </CardHeader>
                        </Card>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => void commands.staffHire("assistant_manager")}>Hire Assistant</Button>
                        <Button
                            variant="outline"
                            onClick={() => {
                                if (clubStaff[0]) {
                                    void commands.staffFire(clubStaff[0].id);
                                }
                            }}
                        >
                            Fire First Staff
                        </Button>
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <CardTitle>Department Snapshot</CardTitle>
                    <CardDescription>Capacity and workload</CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead className="text-right">Rating</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {clubStaff.map((member) => (
                                <TableRow key={member.id}>
                                    <TableCell>{member.name}</TableCell>
                                    <TableCell>{member.role}</TableCell>
                                    <TableCell className="text-right"><Badge variant="secondary">{member.rating}</Badge></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
